/**
 * QrPanel — the QR, the direct-download URL as copyable text, and the PDF button.
 *
 * The URL shown here is the one the QR on the printed document encodes. Opening
 * it downloads the PDF directly: no sign-in, no landing page, no redirect (BOE
 * Third.3–4). That is why it is rendered as a plain external link and **never**
 * wrapped in a portal screen of the portal's own.
 *
 * The image comes from the server's own QR endpoint — `qrPath`, which is
 * `/api/v1/decas/{id}/qr` for a goods DeCA and `/api/v1/passenger-decas/{id}/qr`
 * for a route sheet — and is therefore the same bits that go into the PDF. The
 * endpoint is a **prop rather than built from an id here**: the two document
 * families have separate id spaces and separate routes, and a panel that composed
 * the goods path from whatever id it was handed would render a goods QR request
 * for a route sheet, 404, and fall through to the local draw — which succeeds, so
 * nothing would look broken.
 *
 * If that call fails — offline, or a mock with no image behind it — the panel falls
 * back to drawing the QR locally from `directDownloadUrl`. Both encode exactly the
 * same string, so the fallback cannot disagree with the document; it only removes a
 * blank box from the review build.
 */

import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import QRCode from 'qrcode'

import { Button, CopyButton, Notice } from './primitives.tsx'
import { Icon } from './Icon.tsx'
import { fetchBinary, MIME } from '../app/api.ts'
import { formatCsv } from '../deca/format.ts'

export function QrPanel({
  qrPath,
  documentNumber,
  directDownloadUrl,
  csv,
  live,
  expiresLabel,
  onDownloadPdf,
}: {
  /** The authenticated endpoint that renders this document's QR, query string included. */
  qrPath: string
  documentNumber: string | null | undefined
  directDownloadUrl: string | null | undefined
  csv: string | null | undefined
  /** Whether `/d/{token}` still serves — drives the notice, not the URL itself. */
  live: boolean
  expiresLabel?: string
  onDownloadPdf: () => void
}) {
  const { t } = useTranslation()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [serverQr, setServerQr] = useState<string | null>(null)

  useEffect(() => {
    let objectUrl: string | null = null
    let cancelled = false

    void fetchBinary(qrPath, MIME.png)
      .then((blob) => {
        if (cancelled) return
        objectUrl = URL.createObjectURL(blob)
        setServerQr(objectUrl)
      })
      .catch(() => {
        if (!cancelled) setServerQr(null)
      })

    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [qrPath])

  useEffect(() => {
    const canvas = canvasRef.current
    if (serverQr || !directDownloadUrl || !canvas) return
    void QRCode.toCanvas(canvas, directDownloadUrl, {
      errorCorrectionLevel: 'M',
      margin: 1,
      // Twice the display size, so the code stays crisp on a HiDPI screen and
      // when the panel is printed.
      width: 336,
      color: { dark: '#000000', light: '#ffffff' },
    }).then(() => {
      // `qrcode` writes inline width/height, which beats the stylesheet's
      // 168px and blows the panel out. The backing store keeps its resolution.
      canvas.style.removeProperty('width')
      canvas.style.removeProperty('height')
    })
  }, [serverQr, directDownloadUrl])

  return (
    <div className="stack">
      <div className="qr-panel">
        <div className="qr-panel__code">
          {directDownloadUrl ? (
            serverQr ? (
              <img
                src={serverQr}
                alt={t('detail.qrTitle')}
                width={168}
                height={168}
                // A request that returns 200 can still carry something that is
                // not a decodable PNG. Dropping back to the local render keeps
                // the panel showing a scannable code rather than a broken image
                // — both encode the identical `directDownloadUrl`.
                onError={() => setServerQr(null)}
              />
            ) : (
              <canvas ref={canvasRef} aria-label={t('detail.qrTitle')} role="img" />
            )
          ) : (
            <div className="state__icon">
              <Icon name="qr" size={24} />
            </div>
          )}
        </div>

        <div className="qr-panel__body">
          <p className="small muted">{t('detail.qrHelp')}</p>

          {directDownloadUrl ? (
            <div className="stack-sm">
              <span className="label-sm muted">{t('detail.directUrl')}</span>
              {/*
                Named for what it copies and drawn as a real action, because the
                client went looking for this control and did not find it (#83).
                It was a `sm` `ghost` button labelled like every other copy on
                the page. The URL text stays: it is what an inspector reads back
                over the phone.
              */}
              <div className="qr-panel__url">
                <code>{directDownloadUrl}</code>
                <CopyButton
                  value={directDownloadUrl}
                  label={t('detail.copyDownloadLink')}
                  size="md"
                  variant="secondary"
                />
              </div>
              <a
                className="small row qr-panel__open"
                href={directDownloadUrl}
                target="_blank"
                rel="noreferrer noopener"
                style={{ gap: 'var(--space-xs)' }}
              >
                {t('detail.downloadPdf')} <Icon name="externalLink" size={14} />
              </a>
            </div>
          ) : null}

          {csv ? (
            <div className="stack-sm">
              <span className="label-sm muted">{t('detail.csv')}</span>
              <div className="row wrap">
                <span className="csv-code">{formatCsv(csv)}</span>
                <CopyButton value={formatCsv(csv)} />
              </div>
            </div>
          ) : null}

          <div className="row wrap">
            <Button icon="download" onClick={onDownloadPdf}>
              {t('detail.downloadPdf')}
            </Button>
            {documentNumber ? <span className="small muted tabular">{documentNumber}</span> : null}
          </div>
        </div>
      </div>

      {live ? (
        expiresLabel ? (
          <Notice tone="info">{expiresLabel}</Notice>
        ) : null
      ) : (
        <Notice tone="neutral">{t('detail.qrExpired')}</Notice>
      )}
    </div>
  )
}
